import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AnimatedPage from '@/components/AnimatedPage';
import { CopyableText } from '@/utils/copy';
import { appealsService } from '@/services/appeals/appeals.service';
import { useNotification } from '@/contexts/NotificationContext';
import type {
  Appeal,
  AppealBacklogStatistics,
  AppealProcessingStatistics,
  AppealFile,
  AppealFileKey,
} from '@/types/appeal';
import WorkClaimButton, {
  type WorkClaimAction,
  type WorkClaimState,
} from '@/components/WorkClaimButton';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { RoleEnum } from '@/types/auth/roles';
import { ActionKey, canPerformAction } from '@/auth/actionMatrix';
import { hasPermission, Permission } from '@/auth/permissions';
import SearchableSelect from '@/components/SearchableSelect';
import { tradersService } from '@/services/traders/traders.service';
import { merchantsService } from '@/services/merchants/merchants.service';
import { getAppealFileKind, getAppealFiles } from '@/utils/appealFiles';

const rejectStatuses = [
  { value: 'WRONG DETAILS', label: 'Неверные реквизиты' },
  { value: 'WRONG BANK', label: 'Неверный банк' },
  { value: 'FAKE CHECK', label: 'Фейковый чек' },
  { value: 'NO FUNDS RECEIVED', label: 'Средства не поступили' },
  { value: 'REFUND TO SENDER', label: 'Возврат отправителю' },
  { value: 'AMOUNT ERROR', label: 'Ошибка суммы' },
];

const getStatusColor = (status?: string | null) => {
  const normalized = String(status || '').toUpperCase();
  if (['SUCCESS', 'PAID', 'CLOSED'].includes(normalized)) return 'success';
  if (
    ['DECLINED', 'CANCELED', 'CANCELLED'].includes(normalized) ||
    rejectStatuses.some((item) => item.value === normalized)
  ) {
    return 'error';
  }
  if (['ACCEPTED', 'INIT'].includes(normalized)) return 'warning';
  return 'default';
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value.endsWith('Z') ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatAmount = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : String(value);
};

const buildAppealStatistics = (appeals: Appeal[], slaMinutes = 120): AppealBacklogStatistics => {
  const now = Date.now();
  const ages = appeals
    .map((appeal) => appeal.date_create)
    .filter((value): value is string => Boolean(value))
    .map((value) => new Date(value.endsWith('Z') ? value : `${value}Z`).getTime())
    .filter((value) => Number.isFinite(value))
    .map((value) => Math.max(0, (now - value) / 60_000))
    .sort((a, b) => a - b);
  const percentile = (fraction: number) => (
    ages.length ? ages[Math.max(0, Math.ceil(ages.length * fraction) - 1)] : 0
  );
  const byStatus: Record<string, number> = {};
  const byDate: Record<string, number> = {};
  let totalAmount = 0;
  for (const appeal of appeals) {
    byStatus[appeal.status || 'UNKNOWN'] = (byStatus[appeal.status || 'UNKNOWN'] || 0) + 1;
    totalAmount += Number(appeal.amount || 0);
    if (appeal.date_create) {
      const date = new Date(appeal.date_create.endsWith('Z') ? appeal.date_create : `${appeal.date_create}Z`);
      if (!Number.isNaN(date.getTime())) {
        const key = date.toISOString().slice(0, 10);
        byDate[key] = (byDate[key] || 0) + 1;
      }
    }
  }
  const overdue = ages.filter((age) => age > slaMinutes).length;
  return {
    generated_at: new Date().toISOString(),
    open_now: appeals.length,
    overdue,
    overdue_percent: appeals.length ? (overdue * 100) / appeals.length : 0,
    total_amount: totalAmount,
    average_age_minutes: ages.length ? ages.reduce((sum, age) => sum + age, 0) / ages.length : 0,
    median_age_minutes: percentile(0.5),
    p90_age_minutes: percentile(0.9),
    oldest_age_minutes: ages.length ? ages[ages.length - 1] : 0,
    age_buckets: {
      under_15m: ages.filter((age) => age < 15).length,
      from_15m_to_30m: ages.filter((age) => age >= 15 && age < 30).length,
      from_30m_to_60m: ages.filter((age) => age >= 30 && age < 60).length,
      from_1h_to_3h: ages.filter((age) => age >= 60 && age < 180).length,
      over_3h: ages.filter((age) => age >= 180).length,
    },
    by_status: byStatus,
    created_by_date: Object.entries(byDate)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([date, count]) => ({ date, count })),
  };
};

export default function Appeals() {
  const [appeals, setAppeals] = useState<Appeal[]>([]);
  const [historyAppeals, setHistoryAppeals] = useState<Appeal[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(100);
  const [activePage, setActivePage] = useState(0);
  const [activePageSize, setActivePageSize] = useState(50);
  const [section, setSection] = useState<'active' | 'history'>('active');
  const [allTraders, setAllTraders] = useState<Array<{ id: string; name: string }>>([]);
  const [allMerchants, setAllMerchants] = useState<Array<{ id: string; name: string }>>([]);
  const [statistics, setStatistics] = useState<AppealBacklogStatistics | null>(null);
  const [processingStatistics, setProcessingStatistics] = useState<AppealProcessingStatistics | null>(null);
  const [processingLoading, setProcessingLoading] = useState(true);
  const [processingError, setProcessingError] = useState(false);
  const processingRequest = useRef(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [traderFilter, setTraderFilter] = useState('');
  const [merchantFilter, setMerchantFilter] = useState('');
  const [claimFilter, setClaimFilter] = useState<'all' | 'claimed'>('all');
  const [dateSort, setDateSort] = useState<'newest' | 'oldest'>('newest');
  const [selectedAppeal, setSelectedAppeal] = useState<Appeal | null>(null);
  const [filePreview, setFilePreview] = useState<{
    appealId: number;
    key: AppealFileKey | 'legacy';
    label: string;
    file: AppealFile;
  } | null>(null);
  const [filePreviewFailed, setFilePreviewFailed] = useState(false);
  const [action, setAction] = useState<'success' | 'reject' | null>(null);
  const [amount, setAmount] = useState('');
  const [rejectStatus, setRejectStatus] = useState(rejectStatuses[3].value);
  const [submitting, setSubmitting] = useState(false);
  const { showNotification } = useNotification();
  const user = useSelector((state: RootState) => state.auth.user);
  const canResolveSuccess = canPerformAction(user, ActionKey.APPEALS_RESOLVE_SUCCESS);
  const isAdmin = Boolean(user?.is_superuser || user?.role === RoleEnum.ADMIN);
  const canReject = Boolean(
    isAdmin
    || user?.role === RoleEnum.SUPPORT
    || hasPermission(user, Permission.APPEALS_RESOLVE_REJECT),
  );
  const showsOnlyOwnClaimedAppeals = (
    user?.role === RoleEnum.SUPPORT || hasPermission(user, Permission.APPEALS_SUPPORT)
  ) && !isAdmin;
  const canViewHistory = Boolean(
    isAdmin
    || user?.role === RoleEnum.SUPPORT
    || hasPermission(user, Permission.APPEALS_READ),
  );
  const canResetWork = Boolean(
    user?.is_superuser || user?.role === RoleEnum.ADMIN || user?.username?.toLowerCase() === 'support_ilya',
  );

  const loadProcessingStatistics = async () => {
    const request = ++processingRequest.current;
    setProcessingLoading(true);
    setProcessingError(false);
    try {
      const data = await appealsService.getProcessingStatistics();
      if (request === processingRequest.current) setProcessingStatistics(data);
    } catch (error) {
      if (request === processingRequest.current) {
        setProcessingStatistics(null);
        setProcessingError(true);
      }
      console.error('Appeal processing statistics load error:', error);
    } finally {
      if (request === processingRequest.current) setProcessingLoading(false);
    }
  };

  const loadAppeals = async () => {
    void loadProcessingStatistics();
    try {
      setLoading(true);
      const data = await appealsService.getAppeals({ pending: true });
      setAppeals(data);
      setStatistics(buildAppealStatistics(data, 120));
    } catch (error) {
      console.error('Appeals load error:', error);
      showNotification('Не удалось загрузить апелляции', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAppeals();
    return () => { processingRequest.current += 1; };
  }, []);

  useEffect(() => {
    merchantsService.getMerchants().then((data) => {
      const items = Array.isArray(data) ? data : (data?.items || []);
      setAllMerchants(items.map((item: { id: string; name: string }) => ({
        id: item.id,
        name: item.name,
      })));
    }).catch((error) => console.error('Merchants load error:', error));
  }, []);

  useEffect(() => {
    if (!canViewHistory) return;
    tradersService.getTraders().then((data) => {
      const items = Array.isArray(data) ? data : (data?.items || []);
      setAllTraders(items.map((item: any) => ({
        id: item.id,
        name: item.name || item.username || item.id,
      })));
    }).catch((error) => console.error('Traders load error:', error));
  }, [canViewHistory]);

  const loadHistory = async (page = historyPage, pageSize = historyPageSize) => {
    if (!canViewHistory) return;
    try {
      setLoading(true);
      const data = await appealsService.getHistory({
        trader_id: traderFilter || undefined,
        transaction_id: search.trim() || undefined,
        page: page + 1,
        page_size: pageSize,
      });
      setHistoryAppeals(data.items);
      setHistoryTotal(data.total);
    } catch (error) {
      console.error('Appeal history load error:', error);
      showNotification('Не удалось загрузить историю апелляций', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (section === 'history') loadHistory();
  }, [section, historyPage, historyPageSize]);

  const traderStats = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>();
    appeals.forEach((appeal) => {
      if (appeal.trader_id) {
        const current = map.get(appeal.trader_id);
        map.set(appeal.trader_id, {
          name: appeal.trader_name || appeal.trader_id,
          count: (current?.count || 0) + 1,
        });
      }
    });
    return Array.from(map.entries())
      .map(([id, trader]) => ({ id, ...trader }))
      .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name));
  }, [appeals]);

  const topTraders = traderStats.slice(0, 10);
  const claimedAppealsCount = useMemo(
    () => appeals.filter((appeal) => (
      showsOnlyOwnClaimedAppeals ? appeal.is_claimed_by_me : Boolean(appeal.claimed_by_id)
    )).length,
    [appeals, showsOnlyOwnClaimedAppeals],
  );

  const filteredAppeals = useMemo(() => {
    if (section === 'history') return historyAppeals;
    const normalizedSearch = search.trim().toLowerCase();
    return appeals.filter((appeal) => {
      const matchesSearch =
        !normalizedSearch ||
        String(appeal.id).includes(normalizedSearch) ||
        String(appeal.partner_transaction_id || '').toLowerCase().includes(normalizedSearch) ||
        String(appeal.transaction_id || '').toLowerCase().includes(normalizedSearch);
      const matchesTrader = !traderFilter || appeal.trader_id === traderFilter;
      const matchesMerchant = !merchantFilter || appeal.merchant_id === merchantFilter;
      const matchesClaim = claimFilter === 'all' || (
        showsOnlyOwnClaimedAppeals ? appeal.is_claimed_by_me : Boolean(appeal.claimed_by_id)
      );
      return matchesSearch && matchesTrader && matchesMerchant && matchesClaim;
    }).sort((left, right) => {
      const leftDate = Date.parse(left.date_create || '');
      const rightDate = Date.parse(right.date_create || '');
      if (Number.isNaN(leftDate)) return Number.isNaN(rightDate) ? right.id - left.id : 1;
      if (Number.isNaN(rightDate)) return -1;
      const difference = leftDate - rightDate || left.id - right.id;
      return dateSort === 'oldest' ? difference : -difference;
    });
  }, [
    appeals,
    claimFilter,
    dateSort,
    historyAppeals,
    merchantFilter,
    search,
    section,
    showsOnlyOwnClaimedAppeals,
    traderFilter,
  ]);

  const activePageCount = activePageSize === -1
    ? 1
    : Math.max(1, Math.ceil(filteredAppeals.length / activePageSize));
  const currentActivePage = Math.min(activePage, activePageCount - 1);
  const displayedAppeals = useMemo(() => {
    if (section === 'history') return filteredAppeals;
    if (activePageSize === -1) return filteredAppeals;
    const start = currentActivePage * activePageSize;
    return filteredAppeals.slice(start, start + activePageSize);
  }, [activePageSize, currentActivePage, filteredAppeals, section]);

  const updateAppealClaim = (
    appealId: number,
    result: unknown,
    claimAction: WorkClaimAction,
  ) => {
    let claimUpdate: WorkClaimState;
    if (claimAction === 'claim') {
      if (!result || typeof result !== 'object') {
        void loadAppeals();
        return;
      }
      const response = result as Record<string, unknown>;
      claimUpdate = {
        claimed_by_id: typeof response.claimed_by_id === 'string' ? response.claimed_by_id : null,
        claimed_by_name: typeof response.claimed_by_name === 'string' ? response.claimed_by_name : null,
        claimed_at: typeof response.claimed_at === 'string' ? response.claimed_at : null,
        is_claimed_by_me: response.is_claimed_by_me === true,
      };
    } else {
      claimUpdate = {
        claimed_by_id: null,
        claimed_by_name: null,
        claimed_at: null,
        is_claimed_by_me: false,
      };
    }

    setAppeals((current) => current.map((appeal) => (
      appeal.id === appealId ? { ...appeal, ...claimUpdate } : appeal
    )));
  };

  const openActionDialog = (appeal: Appeal, nextAction: 'success' | 'reject') => {
    if (nextAction === 'success' && !canResolveSuccess) {
      showNotification('Недостаточно прав для закрытия апелляции в успех', 'error');
      return;
    }
    if (!appeal.is_claimed_by_me) {
      showNotification('Сначала возьмите апелляцию на себя', 'error');
      return;
    }
    setSelectedAppeal(appeal);
    setAction(nextAction);
    setAmount(String(appeal.amount || ''));
    setRejectStatus(rejectStatuses[3].value);
  };

  const closeActionDialog = () => {
    if (submitting) return;
    setSelectedAppeal(null);
    setAction(null);
  };

  const submitAction = async () => {
    if (!selectedAppeal || !action) return;

    try {
      setSubmitting(true);
      if (action === 'success') {
        const numericAmount = Number(amount);
        if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
          showNotification('Укажите корректную сумму', 'error');
          return;
        }

        await appealsService.resolveSuccess(selectedAppeal.id, {
          partner_transaction_id: selectedAppeal.partner_transaction_id,
          amount: numericAmount,
        });
        showNotification('Апелляция закрыта, транзакция отправлена в успех', 'success');
      } else {
        await appealsService.reject(selectedAppeal.id, { status: rejectStatus });
        showNotification('Апелляция переведена в отказ', 'success');
      }

      closeActionDialog();
      const remainingAppeals = appeals.filter((appeal) => appeal.id !== selectedAppeal.id);
      setAppeals(remainingAppeals);
      setStatistics(buildAppealStatistics(remainingAppeals, 120));
      void loadProcessingStatistics();
    } catch (error) {
      console.error('Appeal action error:', error);
      showNotification('Не удалось выполнить действие по апелляции', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const filePreviewKind = filePreview
    ? getAppealFileKind(filePreview.file, filePreview.key)
    : 'download';

  const closeFilePreview = () => {
    setFilePreview(null);
    setFilePreviewFailed(false);
  };

  return (
    <AnimatedPage>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Апелляции</Typography>
          <Button variant="contained" onClick={() => section === 'history' ? loadHistory() : loadAppeals()} disabled={loading} startIcon={<RefreshIcon />}>
            Обновить
          </Button>
        </Box>

        {canViewHistory && (
          <Tabs value={section} onChange={(_, value) => { setSection(value); setHistoryPage(0); setActivePage(0); }} sx={{ mb: 3 }}>
            <Tab value="active" label="Активные" />
            <Tab value="history" label="История" />
          </Tabs>
        )}

        {section === 'active' && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Время обработки апелляций · последние 7 дней
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              По всем апелляциям в журнале админки, без фильтров таблицы. Ожидание документов включено.
            </Typography>
            {processingError ? (
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography color="error">Не удалось загрузить время обработки</Typography>
                <Button onClick={() => void loadProcessingStatistics()}>Повторить</Button>
              </Stack>
            ) : (
              <Grid container spacing={2}>
                {[
                  { label: 'Среднее от взятия в работу до решения', value: processingStatistics?.average_processing_minutes },
                  { label: 'Медиана от взятия в работу до решения', value: processingStatistics?.median_processing_minutes },
                ].map((item) => (
                  <Grid item xs={12} sm={4} key={item.label}>
                    <Tooltip title="Для каждой обработки: первое решение минус последнее взятие в работу тем же сотрудником. Передача, освобождение и сброс прерывают интервал. Среднее — сумма интервалов / их количество; медиана — середина отсортированных интервалов.">
                      <Box>
                        <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                        <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
                          {processingLoading ? 'Загрузка…' : item.value == null ? '—' : `${item.value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} мин.`}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Grid>
                ))}
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Обработок в расчёте</Typography>
                  <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
                    {processingLoading ? 'Загрузка…' : `${processingStatistics?.measured_count ?? 0} из ${processingStatistics?.total_decisions ?? 0} решений`}
                  </Typography>
                  {!processingLoading && (
                    <Typography variant="body2" color="text.secondary">
                      Исключено без корректной пары: {processingStatistics?.excluded_count ?? 0}.
                      {processingStatistics?.measured_count === 0 && ' Нет данных для расчёта времени.'}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                    <Typography variant="subtitle2">От создания апелляции до закрытия</Typography>
                    <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
                      {processingLoading ? 'Загрузка…' : processingStatistics?.average_lifetime_minutes == null ? '—' : `${processingStatistics.average_lifetime_minutes.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} мин. в среднем`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Включает ожидание до взятия в работу и ожидание документов. По последнему закрытию каждой апелляции за последние 7 дней, без фильтров таблицы.
                    </Typography>
                    {!processingLoading && <Typography variant="body2" color="text.secondary">
                      В расчёте: {processingStatistics?.lifetime_measured_count ?? 0} из {processingStatistics?.lifetime_total_closed ?? 0} закрытых апелляций.
                      {' '}Исключено без корректной даты создания: {processingStatistics?.lifetime_excluded_count ?? 0}.
                      {' '}Дата создания сохраняется при закрытии; для старых решений она может отсутствовать.
                    </Typography>}
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        )}

        {section === 'active' && statistics && <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Открыто сейчас', value: statistics.open_now, color: 'primary.main' },
            { label: 'Старше SLA 2 часа', value: statistics.overdue, color: statistics.overdue ? 'error.main' : 'success.main' },
            { label: 'Средний возраст', value: `${Math.round(statistics.average_age_minutes)} мин.`, color: 'text.primary' },
            { label: '90% не старше', value: `${Math.round(statistics.p90_age_minutes)} мин.`, color: statistics.p90_age_minutes > 120 ? 'error.main' : 'text.primary' },
            { label: 'Самая старая', value: `${Math.round(statistics.oldest_age_minutes)} мин.`, color: statistics.oldest_age_minutes > 60 ? 'error.main' : 'text.primary' },
            { label: 'Сумма в апелляциях', value: `${formatAmount(statistics.total_amount)} USDT`, color: 'text.primary' },
          ].map((item) => <Grid item xs={12} sm={6} lg={2} key={item.label}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="body2" color="text.secondary">{item.label}</Typography>
              <Typography variant="h5" sx={{ mt: 1, fontWeight: 700, color: item.color }}>{item.value}</Typography>
            </Paper>
          </Grid>)}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Возраст открытых апелляций</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Chip label={`до 15 мин: ${statistics.age_buckets.under_15m}`} color="success" variant="outlined" />
                <Chip label={`15–30 мин: ${statistics.age_buckets.from_15m_to_30m}`} color="warning" variant="outlined" />
                <Chip label={`30–60 мин: ${statistics.age_buckets.from_30m_to_60m}`} color="warning" />
                <Chip label={`1–3 часа: ${statistics.age_buckets.from_1h_to_3h}`} color="error" variant="outlined" />
                <Chip label={`более 3 часов: ${statistics.age_buckets.over_3h}`} color="error" />
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Открытый backlog по дате создания</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {statistics.created_by_date.length
                  ? statistics.created_by_date.slice(-14).map(item => <Chip key={item.date} label={`${new Date(`${item.date}T00:00:00`).toLocaleDateString()}: ${item.count}`} variant="outlined" />)
                  : <Typography color="text.secondary">Нет данных</Typography>}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>По статусам</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {Object.entries(statistics.by_status).map(([status, count]) => <Chip key={status} label={`${status}: ${count}`} />)}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Топ-10 трейдеров по количеству апелляций</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack spacing={0.5}>
                  {topTraders.map((trader, index) => (
                    <Button
                      key={trader.id}
                      color="inherit"
                      onClick={() => { setTraderFilter(trader.id); setActivePage(0); }}
                      sx={{ justifyContent: 'space-between', textTransform: 'none', px: 1.5 }}
                    >
                      <span>{index + 1}. {trader.name}</span>
                      <Chip size="small" color="primary" variant="outlined" label={trader.count} />
                    </Button>
                  ))}
                  {topTraders.length === 0 && (
                    <Typography color="text.secondary">Нет данных</Typography>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={section === 'active' ? 2 : 6}>
              <TextField
                fullWidth
                label="ID апелляции, заявки или транзакции"
                value={search}
                onChange={(event) => { setSearch(event.target.value); setActivePage(0); }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && section === 'history') {
                    setHistoryPage(0);
                    loadHistory(0);
                  }
                }}
              />
            </Grid>
            {section === 'active' && <Grid item xs={12} md={2}>
              <SearchableSelect
                label="Мерчант"
                value={merchantFilter}
                options={allMerchants.map((merchant) => ({
                  id: merchant.id,
                  label: merchant.name,
                }))}
                onChange={(value) => { setMerchantFilter(value); setActivePage(0); }}
              />
            </Grid>}
            <Grid item xs={12} md={section === 'active' ? 2 : 4}>
              <SearchableSelect
                label="Трейдер"
                value={traderFilter}
                options={(section === 'history' ? allTraders : traderStats).map((trader) => ({
                  id: trader.id,
                  label: trader.name,
                }))}
                onChange={(value) => { setTraderFilter(value); setActivePage(0); }}
              />
            </Grid>
            {section === 'active' && <Grid item xs={12} md={2}>
              <ButtonGroup fullWidth sx={{ height: '56px' }} aria-label="Фильтр по взятым апелляциям">
                <Button
                  variant={claimFilter === 'all' ? 'contained' : 'outlined'}
                  onClick={() => { setClaimFilter('all'); setActivePage(0); }}
                >
                  Все ({appeals.length})
                </Button>
                <Button
                  variant={claimFilter === 'claimed' ? 'contained' : 'outlined'}
                  onClick={() => { setClaimFilter('claimed'); setActivePage(0); }}
                >
                  {showsOnlyOwnClaimedAppeals ? 'Мои взятые' : 'Взятые'} ({claimedAppealsCount})
                </Button>
              </ButtonGroup>
            </Grid>}
            {section === 'active' && <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel id="appeals-date-sort-label">По дате создания</InputLabel>
                <Select
                  labelId="appeals-date-sort-label"
                  label="По дате создания"
                  value={dateSort}
                  onChange={(event) => {
                    setDateSort(event.target.value as 'newest' | 'oldest');
                    setActivePage(0);
                  }}
                >
                  <MenuItem value="newest">Сначала новые</MenuItem>
                  <MenuItem value="oldest">Сначала старые</MenuItem>
                </Select>
              </FormControl>
            </Grid>}
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="outlined" sx={{ height: '56px' }} onClick={() => {
                if (section === 'history') {
                  setHistoryPage(0);
                  loadHistory(0);
                } else {
                  setSearch('');
                  setTraderFilter('');
                  setMerchantFilter('');
                  setClaimFilter('all');
                  setDateSort('newest');
                  setActivePage(0);
                }
              }}>
                {section === 'history' ? 'Найти' : 'Сбросить'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID апелляции</TableCell>
                <TableCell>Мерчант</TableCell>
                <TableCell>Трейдер</TableCell>
                <TableCell>Сумма</TableCell>
                <TableCell>ID заявки</TableCell>
                <TableCell>Статус транзакции</TableCell>
                <TableCell>Статус апелляции</TableCell>
                <TableCell>Создана</TableCell>
                <TableCell>Файл</TableCell>
                {section === 'active' && <TableCell>Ответственный</TableCell>}
                {section === 'active' && <TableCell align="right">Действия</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAppeals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={section === 'active' ? 11 : 9} align="center">
                    {loading ? 'Загрузка...' : 'Апелляций нет'}
                  </TableCell>
                </TableRow>
              ) : (
                displayedAppeals.map((appeal) => (
                  <TableRow key={appeal.id} hover>
                    <TableCell><CopyableText value={String(appeal.id)} copiedMessage="ID апелляции скопирован" /></TableCell>
                    <TableCell>{appeal.merchant_name || allMerchants.find((merchant) => merchant.id === appeal.merchant_id)?.name || appeal.merchant_id || '—'}</TableCell>
                    <TableCell>{appeal.trader_name || appeal.trader_id || '-'}</TableCell>
                    <TableCell>{formatAmount(appeal.amount)}</TableCell>
                    <TableCell><CopyableText value={appeal.partner_transaction_id} copiedMessage="ID заявки скопирован" /></TableCell>
                    <TableCell>
                      <Chip size="small" label={appeal.transaction_status || '-'} color={getStatusColor(appeal.transaction_status) as any} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={appeal.status || '-'} color={getStatusColor(appeal.status) as any} />
                    </TableCell>
                    <TableCell>{formatDate(appeal.date_create)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {getAppealFiles(appeal).map(({ key, label, file }) => (
                          <Tooltip key={key} title={`Открыть: ${label.toLowerCase()}`}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<InsertDriveFileIcon />}
                              onClick={() => {
                                setFilePreview({ appealId: appeal.id, key, label, file });
                                setFilePreviewFailed(false);
                              }}
                            >
                              {label}
                            </Button>
                          </Tooltip>
                        ))}
                        {getAppealFiles(appeal).length === 0 && (
                          <Typography variant="body2" color="text.secondary">Нет файлов</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    {section === 'active' && <TableCell sx={{ minWidth: 170 }}>
                      <WorkClaimButton
                        claim={appeal}
                        onClaim={() => appealsService.claim(appeal.id)}
                        onRelease={() => appealsService.release(appeal.id)}
                        onAdminReset={() => appealsService.resetWorkState(appeal.id)}
                        canAdminReset={canResetWork}
                        onChanged={(result, claimAction) => {
                          updateAppealClaim(appeal.id, result, claimAction);
                        }}
                        onError={(error) => {
                          const detail = (error as any)?.response?.data?.detail;
                          showNotification(detail || 'Не удалось изменить ответственного', 'error');
                        }}
                      />
                    </TableCell>}
                    {section === 'active' && <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {canResolveSuccess && <Tooltip title={appeal.transaction_id ? 'Перевести в успех' : 'Связанная транзакция не найдена'}>
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              disabled={!appeal.transaction_id || !appeal.is_claimed_by_me}
                              onClick={() => openActionDialog(appeal, 'success')}
                            >
                              Успех
                            </Button>
                          </span>
                        </Tooltip>}
                        {canReject && <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<CloseIcon />}
                            disabled={!appeal.is_claimed_by_me}
                            onClick={() => openActionDialog(appeal, 'reject')}
                          >
                            Отказ
                          </Button>}
                      </Stack>
                    </TableCell>}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {section === 'active' && (
          <TablePagination
            component="div"
            count={filteredAppeals.length}
            showLastButton
            page={currentActivePage}
            rowsPerPage={activePageSize}
            onPageChange={(_, page) => setActivePage(page)}
            onRowsPerPageChange={(event) => {
              setActivePageSize(Number(event.target.value));
              setActivePage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, { label: 'Все', value: -1 }]}
            labelRowsPerPage="На странице"
          />
        )}

        {section === 'history' && (
          <TablePagination
            component="div"
            count={historyTotal}
            showLastButton
            page={historyPage}
            rowsPerPage={historyPageSize}
            onPageChange={(_, page) => setHistoryPage(page)}
            onRowsPerPageChange={(event) => {
              setHistoryPageSize(Number(event.target.value));
              setHistoryPage(0);
            }}
            rowsPerPageOptions={[50, 100, 250]}
            labelRowsPerPage="На странице"
          />
        )}

        <Dialog open={!!selectedAppeal && !!action} onClose={closeActionDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{action === 'success' ? 'Перевод апелляции в успех' : 'Отказ по апелляции'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Апелляция #{selectedAppeal?.id}, заявка {selectedAppeal?.partner_transaction_id}
              </Typography>
              {action === 'success' ? (
                <TextField
                  fullWidth
                  label="Сумма апелляции"
                  type="number"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  inputProps={{ min: 0.01, step: 0.01 }}
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Причина отказа</InputLabel>
                  <Select label="Причина отказа" value={rejectStatus} onChange={(event) => setRejectStatus(event.target.value)}>
                    {rejectStatuses.map((item) => (
                      <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeActionDialog} disabled={submitting}>Отмена</Button>
            <Button variant="contained" color={action === 'success' ? 'success' : 'error'} onClick={submitAction} disabled={submitting}>
              Подтвердить
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!filePreview}
          onClose={closeFilePreview}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {filePreview?.label} — апелляция #{filePreview?.appealId}
          </DialogTitle>
          <DialogContent>
            {filePreview && filePreviewKind === 'image' && !filePreviewFailed && (
              <Box
                component="img"
                src={filePreview.file.url}
                alt={`${filePreview.label}, appeal ${filePreview.appealId}`}
                onError={() => setFilePreviewFailed(true)}
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: '75vh',
                  mx: 'auto',
                  objectFit: 'contain',
                }}
              />
            )}
            {filePreview && filePreviewKind === 'video' && (
              <Box
                component="video"
                src={filePreview.file.url}
                controls
                sx={{
                  display: 'block',
                  width: '100%',
                  maxHeight: '75vh',
                  bgcolor: 'black',
                }}
              />
            )}
            {filePreview && filePreviewKind === 'pdf' && (
              <Box
                component="iframe"
                src={filePreview.file.url}
                title={`${filePreview.label}, appeal ${filePreview.appealId}`}
                sx={{
                  width: '100%',
                  height: '75vh',
                  border: 0,
                  bgcolor: 'background.default',
                }}
              />
            )}
            {filePreview && (
              ['archive', 'download'].includes(filePreviewKind) ||
              (filePreviewKind === 'image' && filePreviewFailed)
            ) && (
              <Stack spacing={2} alignItems="flex-start">
                <Typography>
                  Предпросмотр для этого формата недоступен. Файл можно скачать.
                </Typography>
                <Button
                  variant="contained"
                  href={filePreview.file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  startIcon={<InsertDriveFileIcon />}
                >
                  Скачать файл
                </Button>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeFilePreview}>Закрыть</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AnimatedPage>
  );
}
